import { motion } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Hero() {
  return (
    <section className="relative min-h-screen bg-white overflow-hidden pt-24 md:pt-28">
      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-foreground-50/20 pointer-events-none" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT */}
          <div className="order-2 lg:order-1" data-product-shop>
            <ScrollReveal>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-6">
                <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                Biblioteca Premium de IA
              </span>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-foreground-950 leading-[1.1] mb-6">
                +<span className="text-gradient-red">1500 Prompts</span> para Crear Anuncios que Venden
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
                Accede a una biblioteca profesional con más de 1500 prompts optimizados para ChatGPT y otras inteligencias artificiales. Diseñados para ayudarte a crear anuncios de alto impacto para Meta Ads, TikTok Ads y redes sociales en cuestión de minutos.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  'Compatible con ChatGPT',
                  'Compatible con Gemini',
                  'Compatible con Leonardo',
                ].map((item) => (
                  <span key={item} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-50 border border-foreground-200 text-foreground-700 text-xs font-medium">
                    <i className="ri-check-line text-primary-500 text-sm" />
                    {item}
                  </span>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <div className="flex items-baseline gap-3 mb-8">
                <span className="text-4xl md:text-5xl font-bold text-foreground-950">$7.9 USD</span>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="#final-cta"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-primary-500 text-white font-semibold text-base hover:bg-primary-600 transition-all duration-300 shadow-[0_4px_20px_rgba(229,57,53,0.25)] hover:shadow-[0_8px_30px_rgba(229,57,53,0.35)] hover:-translate-y-0.5 whitespace-nowrap"
                >
                  Descargar Ahora
                  <i className="ri-download-line" />
                </a>
                <a
                  href="#contenido"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white border border-foreground-200 text-foreground-800 font-semibold text-base hover:bg-foreground-50 hover:border-foreground-300 transition-all duration-300 whitespace-nowrap"
                >
                  Ver Contenido
                  <i className="ri-arrow-down-line" />
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.5}>
              <div className="flex flex-wrap items-center gap-5 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line text-primary-500" />
                  Descarga inmediata
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-shield-check-line text-primary-500" />
                  Pago seguro
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-infinity-line text-primary-500" />
                  Acceso de por vida
                </span>
              </div>
            </ScrollReveal>
          </div>

          {/* RIGHT - Floating composition */}
          <div className="order-1 lg:order-2 relative h-[420px] md:h-[520px] lg:h-[580px]">
            {/* Main product card */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-[360px] md:w-72 md:h-[400px] bg-white rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-foreground-100 flex flex-col overflow-hidden z-10"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="h-28 bg-gradient-to-br from-primary-500 to-primary-600 flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-accent-500/20 to-transparent" />
                <span className="text-white font-heading text-3xl font-bold relative z-10">1500+</span>
                <span className="text-white/80 text-xs font-medium relative z-10 mt-1">Prompts Publicitarios</span>
              </div>
              <div className="flex-1 p-5 space-y-3">
                <div className="h-2 bg-foreground-100 rounded-full w-3/4" />
                <div className="h-2 bg-foreground-100 rounded-full w-full" />
                <div className="h-2 bg-foreground-100 rounded-full w-5/6" />
                <div className="h-2 bg-foreground-100 rounded-full w-2/3" />
                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center text-[10px] font-bold text-foreground-500">GPT</div>
                    <div className="h-2 bg-foreground-100 rounded-full w-28" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center text-[10px] font-bold text-foreground-500">Gem</div>
                    <div className="h-2 bg-foreground-100 rounded-full w-24" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center text-[10px] font-bold text-foreground-500">Meta</div>
                    <div className="h-2 bg-foreground-100 rounded-full w-20" />
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-foreground-100">
                <div className="h-9 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl w-full flex items-center justify-center">
                  <span className="text-white text-xs font-semibold">Descargar Ahora</span>
                </div>
              </div>
            </motion.div>

            {/* Floating prompt card 1 */}
            <motion.div
              className="absolute top-6 right-2 md:right-6 w-44 bg-white/90 backdrop-blur-sm rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-foreground-100 p-3.5 z-20"
              animate={{ y: [0, -15, 0], rotate: [0, 2, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-facebook-fill text-primary-500 text-xs" />
                </div>
                <span className="text-xs font-semibold text-foreground-700">Meta Ads</span>
              </div>
              <p className="text-[11px] text-foreground-500 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                Crea 5 headlines persuasivos para una campaña de conversión en Facebook dirigida a emprendedores...
              </p>
            </motion.div>

            {/* Floating prompt card 2 */}
            <motion.div
              className="absolute bottom-20 left-0 md:left-2 w-44 bg-white/90 backdrop-blur-sm rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-foreground-100 p-3.5 z-20"
              animate={{ y: [0, 12, 0], rotate: [0, -1.5, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-google-fill text-accent-600 text-xs" />
                </div>
                <span className="text-xs font-semibold text-foreground-700">Meta Ads</span>
              </div>
              <p className="text-[11px] text-foreground-500 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                Genera 20 keywords de alta intención para campaña de servicios profesionales en ciudad capital...
              </p>
            </motion.div>

            {/* Floating prompt card 3 */}
            <motion.div
              className="absolute top-1/3 left-0 md:left-4 w-40 bg-white/90 backdrop-blur-sm rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-foreground-100 p-3 z-20"
              animate={{ y: [0, -10, 0], x: [0, 5, 0] }}
              transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-foreground-100 flex items-center justify-center">
                  <i className="ri-music-fill text-foreground-800 text-xs" />
                </div>
                <span className="text-xs font-semibold text-foreground-700">TikTok</span>
              </div>
              <p className="text-[11px] text-foreground-500 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                Escribe un hook viral para video de producto en los primeros 3 segundos...
              </p>
            </motion.div>

            {/* AI platform badges */}
            <motion.div
              className="absolute bottom-28 right-4 md:right-10 w-14 h-14 rounded-xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-foreground-100 flex items-center justify-center z-20"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
            >
              <span className="text-[9px] font-bold text-foreground-700 text-center leading-tight">Chat<br />GPT</span>
            </motion.div>

            <motion.div
              className="absolute top-16 left-6 md:left-14 w-12 h-12 rounded-xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-foreground-100 flex items-center justify-center z-20"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
            >
              <span className="text-[9px] font-bold text-foreground-700 text-center leading-tight">Gemini</span>
            </motion.div>

            <motion.div
              className="absolute bottom-8 right-20 md:right-28 w-12 h-12 rounded-xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-foreground-100 flex items-center justify-center z-20"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
            >
              <span className="text-[9px] font-bold text-foreground-700 text-center leading-tight">Claude</span>
            </motion.div>

            {/* Platform badges */}
            <motion.div
              className="absolute top-8 left-16 md:left-24 px-2.5 py-1.5 rounded-lg bg-white shadow-[0_4px_16px_rgba(0,0,0,0.05)] border border-foreground-100 flex items-center gap-1.5 z-20"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
            >
              <i className="ri-facebook-fill text-primary-500 text-xs" />
              <span className="text-[10px] font-medium text-foreground-600">Meta</span>
            </motion.div>

            <motion.div
              className="absolute bottom-40 right-0 md:right-4 px-2.5 py-1.5 rounded-lg bg-white shadow-[0_4px_16px_rgba(0,0,0,0.05)] border border-foreground-100 flex items-center gap-1.5 z-20"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            >
              <i className="ri-google-fill text-accent-600 text-xs" />
              <span className="text-[10px] font-medium text-foreground-600">Google</span>
            </motion.div>

            {/* Glow effects */}
            <div className="absolute top-1/4 right-1/4 w-40 h-40 bg-primary-500/8 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-1/4 left-1/4 w-32 h-32 bg-accent-500/8 rounded-full blur-3xl pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}